import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback } from 'react';
import * as PropTypes from 'prop-types';
import { Text, Icon, Label, Stack, ChoiceGroup } from '@fluentui/react';
import { Constants } from '@shared/utils/constants';
import { IAvailabilityActions } from '@shared/Models';
import { convertTermToString } from '@shared/utils/checkoutUtils';
import Tooltip from '@shared/components/tooltip';
import { addFuturePricing } from '@shared/utils/pricing';
const pbTelemetry = Constants.Telemetry.PurchaseBlendsTelemetry;
export const GenerateLabelAndTooltip = ({ context })=>{
    return /*#__PURE__*/ _jsx(Label, {
        className: "mt-20",
        children: /*#__PURE__*/ _jsx(Stack, {
            horizontal: true,
            verticalAlign: "center",
            wrap: false,
            children: /*#__PURE__*/ _jsxs(Text, {
                className: "ms-fontWeight-semibold ",
                children: [
                    `${context.loc('Checkout_PriceAndPaymentOptions', 'Price/payment options')} `,
                    /*#__PURE__*/ _jsx(Tooltip, {
                        tooltipHostProps: {
                            content: context.loc('Checkout_PriceAndBilling_Tooltip_PaymentOptions', "The plan's price and payment frequency"),
                            calloutProps: {
                                gapSpace: 0
                            }
                        },
                        children: /*#__PURE__*/ _jsx(Icon, {
                            iconName: "Info",
                            ariaLabel: context.loc('Checkout_PriceAndBilling_Tooltip_PaymentOptions', "The plan's price and payment frequency")
                        })
                    }),
                    /*#__PURE__*/ _jsx(Text, {
                        className: "requiredColor",
                        children: ` *`
                    })
                ]
            })
        })
    });
};
export const getBillingFrequencyOptions = ({ billingCountryCode, items, futurePriceMonthly, futurePriceYearly, locale }, context)=>{
    const { product, id, termDuration, hasFreeTrial } = items[0];
    const plan = product.getPlanByPlanId(id);
    const availability = plan.availabilities.find((availability)=>{
        const { actions } = availability;
        return actions.includes(IAvailabilityActions.Renew) && actions.includes(IAvailabilityActions.Browse);
    });
    const termsByBillingTermDuration = availability.terms.filter((term)=>term.termUnits === termDuration);
    const billingFrequencyOptions = termsByBillingTermDuration.map((term)=>{
        const termEntity = product.getTermByTermId({
            planId: id,
            termId: term.termId
        });
        if (!termEntity) {
            return null;
        }
        return {
            // If there's no free trial termId, assign the renew termId
            key: termEntity.freeTrialTermId || termEntity.renewTermId,
            // This value isn't displayed, but rather used to pass in the relevant contract term in the case there's only one option
            value: termEntity.renewTermUnits,
            text: convertTermToString({
                context,
                term: termEntity,
                countryCode: billingCountryCode,
                isQuantifiable: items[0].isQuantifiable,
                hasFreeTrial,
                locale
            }),
            onRenderLabel: (option, defaultRender)=>/*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        defaultRender(option),
                        addFuturePricing(term.renewTermUnits, futurePriceMonthly, futurePriceYearly)
                    ]
                })
        };
    }).filter(Boolean);
    return billingFrequencyOptions;
};
export const getDefaultOrSelectedBillingFrequency = ({ items }, options)=>{
    var _items_, _items_1;
    const availableTermFrequencies = options === null || options === void 0 ? void 0 : options.map((option)=>option.key);
    const defaultBillingFrequency = (availableTermFrequencies === null || availableTermFrequencies === void 0 ? void 0 : availableTermFrequencies.includes((_items_ = items[0]) === null || _items_ === void 0 ? void 0 : _items_.termId)) ? (_items_1 = items[0]) === null || _items_1 === void 0 ? void 0 : _items_1.termId : availableTermFrequencies[0];
    return defaultBillingFrequency || '';
};
export const BillingFrequencyChoiceGroup = (props, context)=>{
    const { setSelectedTerm, futurePriceMonthly, futurePriceYearly, billingCountryCode, items, locale } = props;
    const { termId, id } = items[0];
    const options = useCallback(()=>getBillingFrequencyOptions({
            billingCountryCode,
            items,
            futurePriceMonthly,
            futurePriceYearly,
            locale
        }, context), [
        billingCountryCode,
        items,
        futurePriceMonthly,
        futurePriceYearly,
        context,
        locale
    ]);
    const { text, value } = options()[0];
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(GenerateLabelAndTooltip, {
                context: context
            }),
            /*#__PURE__*/ _jsx(Stack, {
                horizontal: true,
                tokens: {
                    childrenGap: 10
                },
                children: /*#__PURE__*/ _jsx(Stack.Item, {
                    children: options().length > 1 ? /*#__PURE__*/ _jsx(ChoiceGroup, {
                        "data-bi-id": pbTelemetry.PickBillingFrequency,
                        "data-bi-name": pbTelemetry.BillingFrequencyComponent,
                        options: options(),
                        onChange: (_, option)=>setSelectedTerm({
                                id,
                                termId: option.key
                            }),
                        selectedKey: termId,
                        defaultSelectedKey: getDefaultOrSelectedBillingFrequency(props, options()),
                        ariaLabelledBy: 'billTermSelection'
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Text, {
                                children: text
                            }),
                            addFuturePricing(value, futurePriceMonthly, futurePriceYearly)
                        ]
                    })
                })
            })
        ]
    });
};
BillingFrequencyChoiceGroup.contextTypes = {
    locParams: PropTypes.func,
    loc: PropTypes.func
};
